// Buyer-oriented guides over the canonical 115-capability catalogue.
//
// These records improve discovery for school owners without creating a second product inventory:
// every linked capability remains owned by MODULE_CATALOG and its `/modules/:key` scope page.
// The three under-marketed product areas (Alumni, HPC and APAAR) deliberately carry their limits
// beside the positive scope so adjacent competitor claims cannot leak into public copy.

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

const guide = (definition) => ({
  ...definition,
  path: `/modules/guides/${definition.key}`,
});

const GUIDES = [
  guide({
    key: "assignment-notices",
    title: "Assignment & Notices",
    shortTitle: "Assignment & Notices",
    eyebrow: "Teaching communication guide",
    type: "buyer-guide",
    status: "Reviewed catalogue guide",
    summary: "Plan class work, record its progress and publish school communication through the appropriate reviewed workflow instead of mixing every message into one channel.",
    problem: "Assignments, notices, circulars and reminders often lose ownership when they move between a teacher, the office and a family channel.",
    outcome: "A clearer teaching-to-family hand-off with the assignment record, intended audience and available delivery evidence kept distinct.",
    workflow: [
      {
        title: "Prepare the class work",
        description: "A teacher records the assignment, due date, class or section and the work expected.",
        capabilityKeys: ["homework-and-assignments"],
      },
      {
        title: "Record progress and review",
        description: "Teachers record received or not received, grade where applicable and keep the class record reviewable.",
        capabilityKeys: ["homework-and-assignments", "digital-gradebook"],
      },
      {
        title: "Publish the right school update",
        description: "Use a notice, announcement or circular according to the audience and formality of the message.",
        capabilityKeys: ["notice-board", "announcements", "circular-management"],
      },
      {
        title: "Review configured delivery evidence",
        description: "Configured channels can carry the update; each channel reports only the evidence it actually returns.",
        capabilityKeys: ["homework-updates", "email-notifications", "whatsapp-alerts", "sms-alerts"],
      },
    ],
    primaryCapabilityKeys: [
      "homework-and-assignments", "notice-board", "announcements", "circular-management", "homework-updates",
    ],
    relatedCapabilityKeys: ["digital-gradebook", "email-notifications", "whatsapp-alerts", "sms-alerts"],
    users: ["Teachers", "Academic coordinators", "School office", "Authorised communication teams"],
    whatIncluded: [
      { title: "Teacher-owned assignment records", detail: "Class or section, due date, reminder, received status and grading stay with the reviewed assignment workflow." },
      { title: "Separate publishing surfaces", detail: "Notices, announcements and circulars remain distinct so the school can choose the right audience and record." },
      { title: "Configured outbound channels", detail: "Use supported in-app, email, WhatsApp or SMS paths when the school has completed the relevant setup." },
    ],
    conditions: [
      { title: "No learner self-upload claim", detail: "Current family and student homework views are read-only; teachers record submission status." },
      { title: "No universal circular approval queue", detail: "Publishing and scheduling are covered, but a separate second-approver circular workflow is not asserted." },
      { title: "Channel evidence differs", detail: "SMS does not prove a read state, and email opens are shown only if the configured provider supplies them." },
    ],
    faqs: [
      { q: "Can students upload assignment files themselves?", a: "Not in the current reviewed public scope. Teachers create the work and record whether it was received; student and parent homework views are read-only." },
      { q: "Are notices, announcements and circulars the same record?", a: "No. The guide groups them for evaluation, while each canonical capability page explains its own audience and publishing boundary." },
      { q: "Can Vidyapeeth360 send every update over WhatsApp?", a: "Only when the school has configured an eligible provider, approved templates and consent controls. The same update may use another configured channel instead." },
      { q: "Does an AI draft publish automatically?", a: "No. Supported drafting remains reviewable; an authorised person decides whether to publish or send." },
    ],
    aliases: ["assignments and notices", "homework and notices", "assignment management", "school notice system"],
    searchTerms: ["homework", "notice", "circular", "announcement", "parent update", "teacher assignment"],
    seoDescription: "Review Vidyapeeth360 assignment, homework, notice, announcement and circular workflows, including users, channels and rollout conditions.",
    disclosure: "The guide groups separate teaching and communication capabilities; it does not imply learner uploads, automatic approval or identical evidence across channels.",
  }),

  guide({
    key: "student-management",
    title: "Student Management",
    shortTitle: "Student Management",
    eyebrow: "Student lifecycle guide",
    type: "buyer-guide",
    status: "Reviewed catalogue guide",
    summary: "Connect an authorised student record to class and section allocation, documents, identity, promotion and reviewable reporting across the school lifecycle.",
    problem: "A student record becomes unreliable when admission, allocation, documents, identity and academic progression are maintained in disconnected registers.",
    outcome: "One governed student lifecycle whose related records can be found, reviewed and advanced by the people responsible for each stage.",
    workflow: [
      {
        title: "Create or approve the student record",
        description: "An authorised admission or registration workflow creates the student record after the applicable review.",
        capabilityKeys: ["student-admission", "enquiry-management"],
      },
      {
        title: "Allocate the learner",
        description: "Assign the student to the intended class and section, then preserve that structure for connected academic work.",
        capabilityKeys: ["class-management", "section-management"],
      },
      {
        title: "Maintain evidence and identity",
        description: "Manage permitted documents and produce the applicable school identity artifact through their dedicated workflows.",
        capabilityKeys: ["document-management", "id-card-management"],
      },
      {
        title: "Review progress and advance the year",
        description: "Use scoped reports and the authorised promotion workflow rather than changing lifecycle state informally.",
        capabilityKeys: ["student-reports", "student-performance", "promotion-management"],
      },
    ],
    primaryCapabilityKeys: [
      "student-admission", "class-management", "section-management", "document-management",
      "id-card-management", "promotion-management", "student-reports",
    ],
    relatedCapabilityKeys: ["enquiry-management", "student-performance", "student-attendance", "result-publishing"],
    users: ["Admissions team", "School office", "Class teachers", "Academic coordinators", "Authorised administrators"],
    whatIncluded: [
      { title: "Governed student record lifecycle", detail: "Create, review, allocate and maintain the record through role-scoped product workflows." },
      { title: "Class, section and document context", detail: "Keep the structure and permitted evidence needed by academic and administrative teams." },
      { title: "Identity, progress and promotion links", detail: "Evaluate each linked capability on its own canonical page before rollout." },
    ],
    conditions: [
      { title: "Not one automatic end-to-end decision", detail: "Admission, document review, allocation and promotion retain their own authorised actions." },
      { title: "No arbitrary admission form promise", detail: "The public registration and enquiry flows are reviewed separately from the general Custom Forms capability." },
      { title: "Access follows assigned roles and record scope", detail: "A guide relationship does not give every user access to every linked student record." },
    ],
    faqs: [
      { q: "Does an application create a student immediately?", a: "No. The reviewed registration or admission path creates the student only after the applicable authorised step." },
      { q: "Can the school import existing students?", a: "A reviewed CSV/XLSX student migration path exists. Its supported columns, validation and rollback conditions are confirmed before rollout." },
      { q: "Are student documents verified automatically?", a: "No. The document workflow supports authorised request, upload and review states; it does not make an admission decision." },
      { q: "Is every lifecycle step included in every plan?", a: "No. Plan scope, release status, user permissions and migration requirements are confirmed against the canonical capability pages and written proposal." },
    ],
    aliases: ["student information system", "student information management", "student records", "student lifecycle", "SIS"],
    searchTerms: ["student profile", "class allocation", "section allocation", "documents", "id card", "promotion"],
    seoDescription: "Review Vidyapeeth360 student lifecycle capabilities for admission, allocation, documents, identity, promotion and school reporting.",
    disclosure: "This guide connects existing student capabilities; it does not turn separate authorised steps into an automatic admission or promotion decision.",
  }),

  guide({
    key: "academic-management",
    title: "Academic Management",
    shortTitle: "Academic Management",
    eyebrow: "Teaching and assessment guide",
    type: "buyer-guide",
    status: "Reviewed catalogue guide",
    summary: "Structure subjects and teaching assignments, prepare timetables and lessons, record assessment work and publish school-reviewed results from connected academic records.",
    problem: "Academic teams lose time and confidence when timetable constraints, lesson work, assessments and result publication live in unrelated files.",
    outcome: "A traceable academic cycle from school structure and teaching plan to a reviewed, publishable learner result.",
    workflow: [
      {
        title: "Set the academic structure",
        description: "Configure subjects, teaching assignments, classes, sections, terms and the academic calendar used by later workflows.",
        capabilityKeys: ["subject-management", "teacher-mapping", "academic-calendar"],
      },
      {
        title: "Plan time and teaching",
        description: "Prepare a constraint-checked timetable and maintain lesson, syllabus and study-material context.",
        capabilityKeys: ["timetable-management", "lesson-plan-management", "syllabus-tracking", "study-material"],
      },
      {
        title: "Record class and assessment work",
        description: "Use assignments, gradebook and exam scheduling as distinct reviewable records.",
        capabilityKeys: ["homework-and-assignments", "digital-gradebook", "exam-scheduling"],
      },
      {
        title: "Review and publish the result",
        description: "Apply the configured board/report-card workflow and publish only through an authorised result step.",
        capabilityKeys: ["result-publishing", "academic-reports", "student-performance"],
      },
    ],
    primaryCapabilityKeys: [
      "subject-management", "teacher-mapping", "timetable-management", "lesson-plan-management",
      "homework-and-assignments", "digital-gradebook", "exam-scheduling", "result-publishing",
    ],
    relatedCapabilityKeys: ["academic-calendar", "syllabus-tracking", "study-material", "academic-reports", "student-performance", "online-classes"],
    users: ["Teachers", "Academic coordinators", "Exam team", "Class teachers", "School leadership"],
    whatIncluded: [
      { title: "Connected academic structure", detail: "Subjects, teachers, classes, sections, terms and calendar context support the reviewed work." },
      { title: "Planning and assessment workflows", detail: "Timetable, lessons, assignments, gradebook and exam records remain explicit rather than collapsed into a generic dashboard." },
      { title: "Authorised result publication", detail: "Configured report-card output is reviewed and published through the supported result workflow." },
    ],
    conditions: [
      { title: "Online classes use an external meeting link", detail: "Vidyapeeth360 schedules the link and materials; it does not host the call, recording or native conferencing controls." },
      { title: "Timetable support is constraint-based", detail: "It produces a reviewable draft and ranked substitute options; it is not marketed as an autonomous AI scheduler." },
      { title: "Board configuration matters", detail: "Grading, report-card and result requirements are mapped against the school's board and rollout scope." },
    ],
    faqs: [
      { q: "Does the timetable publish automatically?", a: "No. The constraint solver produces a draft for review; authorised staff decide what is saved and published." },
      { q: "Does Online Classes include a built-in video platform?", a: "No. Teachers supply an external Zoom, Meet or Teams link; Vidyapeeth360 organises the schedule and learning material around it." },
      { q: "Can students submit homework in the app?", a: "The current reviewed scope is teacher-recorded submission status and read-only family/student views, not learner self-upload." },
      { q: "Does one configuration cover every Indian board?", a: "The product supports board-aware grading and reporting, but the school's exact board, stage and template requirements are reviewed before rollout." },
    ],
    aliases: ["academic management system", "academics", "teaching management", "learning management", "school academics"],
    searchTerms: ["timetable", "lesson plan", "gradebook", "exam", "result", "curriculum", "syllabus"],
    seoDescription: "Review Vidyapeeth360 academic workflows for structure, timetable, lessons, assignments, exams, report cards and result publication.",
    disclosure: "The guide groups reviewed academic capabilities; external meeting, board configuration and authorised publication boundaries remain explicit.",
  }),

  guide({
    key: "admission-management",
    title: "Admission Management",
    shortTitle: "Admission Management",
    eyebrow: "Enquiry-to-student guide",
    type: "buyer-guide",
    status: "Reviewed catalogue guide",
    summary: "Capture an enquiry, manage the six-stage admission pipeline, carry permitted documents forward and create the student record through an authorised conversion.",
    problem: "Admission teams need one defensible trail from first enquiry to a created student without losing follow-ups, source context or permitted documents.",
    outcome: "A reviewed enquiry-to-student hand-off with explicit pipeline history, follow-up ownership and an atomic authorised conversion.",
    workflow: [
      {
        title: "Capture the enquiry",
        description: "Record the source, contact context and next follow-up through the public or staff-assisted enquiry flow.",
        capabilityKeys: ["enquiry-management"],
      },
      {
        title: "Advance the six-stage pipeline",
        description: "Move through New, Contacted, Visit Scheduled, Application and the final Admitted or Lost state with governed history.",
        capabilityKeys: ["enquiry-management", "admission-reports"],
      },
      {
        title: "Review registration and permitted evidence",
        description: "Use the supported registration, correction and document-review paths without turning them into an automated admission decision.",
        capabilityKeys: ["student-admission", "document-management"],
      },
      {
        title: "Convert to the student record",
        description: "An authorised conversion records the selected class and section and atomically creates the linked student record.",
        capabilityKeys: ["student-admission", "class-management", "section-management"],
      },
    ],
    primaryCapabilityKeys: ["enquiry-management", "student-admission", "document-management", "admission-reports"],
    relatedCapabilityKeys: ["class-management", "section-management", "custom-forms", "id-card-management"],
    users: ["Admissions team", "Front-office team", "Authorised school administrators", "School leadership"],
    whatIncluded: [
      { title: "Six reviewed lead stages", detail: "New → Contacted → Visit Scheduled → Application → Admitted or Lost, with source and follow-up context." },
      { title: "Registration and correction paths", detail: "Supported applicant status, review and correction steps remain distinct from the CRM lead pipeline." },
      { title: "Authorised student creation", detail: "The supported conversion creates the linked student record and carries eligible evidence forward." },
    ],
    conditions: [
      { title: "No automated admission decision", detail: "There is no public claim for lead scoring, merit ranking, waitlist decisions or rules-based approval/rejection." },
      { title: "Admission forms are fixed, not custom-built", detail: "The fixed public admission/registration flows and general Custom Forms studio are separate capabilities." },
      { title: "No applicant interview or application-fee portal claim", detail: "A scheduled visit is a pipeline stage, not a full interview system; invoice-led fees remain a separate workflow." },
    ],
    faqs: [
      { q: "What are the reviewed admission stages?", a: "New, Contacted, Visit Scheduled, Application and a final Admitted or Lost state." },
      { q: "Does Vidyapeeth360 decide who is admitted?", a: "No. Authorised school users make the decision and run the supported conversion." },
      { q: "Can Aira send follow-ups automatically?", a: "No. The supported admission assistance prepares reviewable follow-up copy; an authorised user decides whether and how it is sent." },
      { q: "Is application-fee collection included in this guide?", a: "No dedicated applicant application-fee checkout is asserted. The finance workspace covers invoice-led fee collection after the relevant school record and invoice exist." },
    ],
    aliases: ["online admission management", "admissions", "admission CRM", "enquiry to admission", "student enrollment"],
    searchTerms: ["lead", "enquiry", "registration", "application", "admitted", "document review", "admission funnel"],
    seoDescription: "Review Vidyapeeth360 enquiry, registration, document and authorised lead-to-student admission workflows for schools.",
    disclosure: "The guide explains the reviewed six-stage and conversion scope; it does not claim automated admission decisions, interview scheduling or applicant-fee checkout.",
  }),

  guide({
    key: "fees-management",
    title: "Fees Management",
    shortTitle: "Fees Management",
    eyebrow: "Invoice-to-evidence guide",
    type: "buyer-guide",
    status: "Reviewed catalogue guide",
    summary: "Configure fee structures, raise invoice-led dues, collect through counter or a school-connected gateway, issue sequential receipts and reconcile provider evidence separately from settlement.",
    problem: "A school cannot trust its fee position when structures, invoices, collection attempts, receipts, relief and settlement evidence are mixed in one spreadsheet.",
    outcome: "A reviewable finance trail from configured charge to invoice, payment evidence, receipt and reconciliation — with provider boundaries visible.",
    workflow: [
      {
        title: "Configure the charge",
        description: "Define fee heads, structures, category rules and applicable installment plans before invoicing.",
        capabilityKeys: ["fee-structure", "category-wise-fees", "installment-setup"],
      },
      {
        title: "Raise and review dues",
        description: "Use invoice-led records to review billed, pending and overdue positions; relief and fines retain governed paths.",
        capabilityKeys: ["fee-collection", "pending-fee-tracking", "discount-and-scholarship", "fine-management"],
      },
      {
        title: "Collect against the invoice",
        description: "Record an authorised counter payment or create an order through the school's connected Razorpay or Cashfree account.",
        capabilityKeys: ["fee-collection", "online-payment", "payment-gateway"],
      },
      {
        title: "Issue and reconcile evidence",
        description: "Create the sequential receipt from accepted evidence, then review provider callback, settlement and exception records separately.",
        capabilityKeys: ["receipt-generation", "accounting-reports", "financial-summary"],
      },
    ],
    primaryCapabilityKeys: [
      "fee-structure", "fee-collection", "online-payment", "pending-fee-tracking", "receipt-generation",
      "payment-gateway", "accounting-reports", "financial-summary",
    ],
    relatedCapabilityKeys: ["discount-and-scholarship", "fine-management", "installment-setup", "refund-management", "fee-reminder", "category-wise-fees"],
    users: ["Fee-desk staff", "Accountants", "Authorised finance reviewers", "School leadership"],
    whatIncluded: [
      { title: "Invoice-led school billing", detail: "Structures, heads, installments and dues support a reviewable student fee position." },
      { title: "Counter and connected-gateway evidence", detail: "Use supported collection paths while keeping the school's provider account and callback evidence explicit." },
      { title: "Receipts, summaries and accounting review", detail: "Sequential receipts and supported reports connect the collection record to later review." },
    ],
    conditions: [
      { title: "Bring your own gateway", detail: "Online collection requires the school's configured Razorpay or Cashfree account; provider charges and settlement remain external evidence." },
      { title: "Late-fee rules are previewed and applied on demand", detail: "Do not describe the current fine workflow as unattended scheduled posting." },
      { title: "Callback is not bank settlement", detail: "Payment acceptance, receipt issuance, settlement timing, deductions and bank credit are separate evidence points." },
    ],
    faqs: [
      { q: "Which online methods are supported?", a: "The reviewed public scope includes invoice-bound UPI, card and net-banking collection through the school's connected gateway. Exact method eligibility and charges follow the written schedule." },
      { q: "Does Vidyapeeth360 control settlement timing?", a: "No. The connected provider and bank control final settlement timing and deductions; the school reviews that evidence during reconciliation." },
      { q: "Are receipts created before provider confirmation?", a: "The workflow uses accepted payment evidence. A payment attempt alone must not be presented as a completed collection." },
      { q: "Does attendance feed payroll or fees automatically?", a: "No cross-domain automatic posting is implied. Each canonical workflow defines its own inputs, permissions and review boundary." },
    ],
    aliases: ["fees management system", "fee management", "school fees", "school billing", "fee collection software"],
    searchTerms: ["invoice", "dues", "payment", "gateway", "receipt", "reconciliation", "fine", "scholarship"],
    seoDescription: "Review Vidyapeeth360 fee structures, invoice-led collection, gateway evidence, receipts, dues and reconciliation workflows.",
    disclosure: "The guide keeps invoice, provider callback, receipt and final settlement evidence distinct; online collection depends on the school's connected gateway.",
  }),

  guide({
    key: "employee-payroll",
    title: "Employee & Payroll",
    shortTitle: "Employee & Payroll",
    eyebrow: "Workforce operations guide",
    type: "buyer-guide",
    status: "Rolling out school by school",
    summary: "Manage controlled employee records, leave and punch evidence, compensation inputs, reviewed salary runs, payslip delivery and statutory work without implying an automatic punch-to-payroll decision.",
    problem: "Workforce records become risky when identity, attendance, leave, compensation and salary evidence are copied between teams without controlled review.",
    outcome: "A role-scoped employee-to-payroll trail whose inputs, salary run, review and delivery evidence remain visible to authorised teams.",
    workflow: [
      {
        title: "Maintain the employee record",
        description: "Keep controlled identity, employment, role and lifecycle context in the staff workspace.",
        capabilityKeys: ["staff-management"],
      },
      {
        title: "Review time and leave evidence",
        description: "Use punch, leave, shift, workload and coverage records according to the school's enabled workforce scope.",
        capabilityKeys: ["staff-attendance", "leave-management", "staff-shift-tracking"],
      },
      {
        title: "Prepare and approve the salary run",
        description: "Authorised payroll users review configured earnings, deductions and statutory inputs before committing the run.",
        capabilityKeys: ["payroll"],
      },
      {
        title: "Deliver and review evidence",
        description: "Make supported payslips and payroll reports available through the configured, role-scoped path.",
        capabilityKeys: ["payroll", "staff-reports", "export-pdf"],
      },
    ],
    primaryCapabilityKeys: ["staff-management", "staff-attendance", "leave-management", "staff-shift-tracking", "payroll", "staff-reports"],
    relatedCapabilityKeys: ["role-and-permission", "teacher-app", "export-pdf", "accounting-reports"],
    users: ["HR administrators", "Payroll team", "Authorised finance reviewers", "School leadership", "Employees through scoped self-service"],
    whatIncluded: [
      { title: "Controlled employee and compensation records", detail: "Maintain the workforce context and configured salary inputs needed by authorised reviewers." },
      { title: "Leave, punch and coverage evidence", detail: "Review these records without silently converting them into a salary decision." },
      { title: "Reviewed payroll run and payslip paths", detail: "Salary calculations, statutory outputs and delivery remain permission- and rollout-controlled." },
    ],
    conditions: [
      { title: "Workspace still rolling out", detail: "HR & Payroll is still rolling out school by school; confirm the enabled scope before planning implementation." },
      { title: "Device attendance has its own dependency", detail: "Employee punch ingestion requires enabled HR & Payroll and Biometric Devices scope plus compatible, configured middleware." },
      { title: "No automatic punch-to-salary feed", detail: "Attendance punches do not create payroll-ready attendance or automatically change salary." },
      { title: "No automatic legal or bank-disbursement guarantee", detail: "Statutory outputs require configured inputs and professional review; provider-backed money movement is not assumed." },
    ],
    faqs: [
      { q: "Does biometric attendance calculate salary automatically?", a: "No. Where HR & Payroll and Biometric Devices are enabled with compatible middleware, matched employee punches remain presence evidence; they do not calculate or change salary automatically." },
      { q: "Can employees view payslips?", a: "A supported employee self-service path exists when the HR & Payroll workspace and applicable permissions are enabled." },
      { q: "Does the system update itself whenever employment law changes?", a: "No automatic legal-update claim is made. Schools configure inputs and authorised professionals review statutory calculations and outputs." },
      { q: "Is payroll available to every school today?", a: "The workspace is rolling out school by school. Availability, data preparation and the enabled sub-scope are confirmed before rollout." },
    ],
    aliases: ["employee management", "staff management", "payroll management", "HRMS", "human resources"],
    searchTerms: ["employee", "staff", "leave", "biometric", "shift", "salary", "payslip", "statutory"],
    seoDescription: "Review Vidyapeeth360 employee records, leave, attendance evidence, payroll runs, payslips and rollout conditions for schools.",
    disclosure: "HR & Payroll is still rolling out school by school and keeps punch evidence, salary decisions, statutory review and disbursement evidence distinct.",
  }),

  guide({
    key: "alumni-management",
    title: "Alumni Management",
    shortTitle: "Alumni Management",
    eyebrow: "Shipped capability guide",
    type: "capability-guide",
    status: "Plan-gated shipped scope · not a separate catalogue count",
    summary: "Maintain a staff-managed alumni directory, link eligible graduation records, organise cohorts, maintain basic alumni events, record staff-managed RSVPs and export the register.",
    problem: "A school needs continuity after graduation without turning a spreadsheet of old contacts into an unsupported social network or fundraising platform.",
    outcome: "A searchable, exportable alumni register with graduation linkage, cohort context and basic event participation records.",
    workflow: [
      {
        title: "Link the graduation record",
        description: "Eligible academic-year graduation work can create or connect the alumni record to its student source.",
        capabilityKeys: ["promotion-management"],
      },
      {
        title: "Maintain the directory",
        description: "Authorised staff manage supported profile fields and search the directory by the available cohort and career context.",
        capabilityKeys: [],
      },
      {
        title: "Organise cohorts and events",
        description: "Use the Alumni workspace's batch/cohort grouping and basic event/RSVP records for reviewed school activity.",
        capabilityKeys: [],
      },
      {
        title: "Export the register",
        description: "Authorised staff can export the supported directory fields for reviewed school use.",
        capabilityKeys: ["export-excel"],
      },
    ],
    primaryCapabilityKeys: [],
    relatedCapabilityKeys: ["promotion-management", "export-excel"],
    users: ["School administration", "Alumni coordinator", "Authorised event team", "School leadership"],
    whatIncluded: [
      { title: "Managed alumni directory", detail: "Supported fields include batch, class, organisation, profession, contact context, city and LinkedIn where entered." },
      { title: "Graduation linkage and cohort context", detail: "The product can preserve the connection to the eligible student source and group records by cohort." },
      { title: "Basic events, RSVP and export", detail: "Use the shipped Alumni workspace for basic event participation and an authorised directory export." },
    ],
    conditions: [
      { title: "Plan-gated product scope", detail: "Alumni availability follows the live plan matrix and the school's written proposal." },
      { title: "No alumni social network", detail: "There is no alumni post feed, reactions, comments, groups, private community or native alumni app in the reviewed scope." },
      { title: "No placement or mentorship platform", detail: "Jobs, recruiters, referrals, mentorship matching and career pipelines are not implemented alumni workflows." },
      { title: "No money in the Alumni capability", detail: "Paid tickets, donations, giving campaigns, tax receipts and fundraising are explicitly outside the current scope." },
    ],
    faqs: [
      { q: "Can alumni update their own profile?", a: "The current reviewed scope is a staff-managed directory; no public alumni self-service profile flow is asserted." },
      { q: "Does the capability support alumni donations?", a: "No. The shipped Alumni capability explicitly has no money workflow, so giving campaigns, donation tracking and tax receipts are not marketed." },
      { q: "Can the school run reunion registrations?", a: "Authorised staff can maintain basic alumni event records and record RSVPs. There is no public attendee self-registration flow, capacity, waitlist, paid-ticket, check-in, feedback or event-analytics claim." },
      { q: "Is there a separate alumni mobile app?", a: "No current public alumni-app claim is made." },
    ],
    aliases: ["alumni management system", "alumni directory", "graduate records", "alumni events", "alumni database"],
    searchTerms: ["alumni", "graduate", "cohort", "reunion", "RSVP", "directory export"],
    seoDescription: "Review Vidyapeeth360 alumni directory, graduation-link, cohort, basic event RSVP and export scope, with clear exclusions.",
    disclosure: "This is a narrow shipped capability guide. It does not claim a community feed, placement, mentorship, giving, paid events or an alumni app.",
  }),

  guide({
    key: "holistic-progress-card",
    title: "Holistic Progress Card",
    shortTitle: "Holistic Progress Card",
    eyebrow: "Configurable reporting guide",
    type: "capability-guide",
    status: "Configurable school-reviewed scope",
    summary: "Configure a school-reviewed progress card using supported academic, attendance and co-scholastic inputs, teacher remarks, branded PDF output and authorised publication.",
    problem: "A whole-child report loses trust when academic marks, attendance, co-scholastic observations and narrative remarks are assembled without a defined school review and publishing boundary.",
    outcome: "A configured, reviewable progress-card artifact whose supported inputs and teacher-approved narrative remain visible before publication.",
    workflow: [
      {
        title: "Configure the reporting structure",
        description: "Set the applicable board/report-card structure, grading context, domains and school branding for the supported template.",
        capabilityKeys: ["result-publishing", "digital-gradebook"],
      },
      {
        title: "Bring supported evidence together",
        description: "Use the available academic, attendance and configured co-scholastic inputs rather than inventing a universal competency score.",
        capabilityKeys: ["student-performance", "attendance-reports", "academic-reports"],
      },
      {
        title: "Review remarks or a draft",
        description: "A teacher reviews remarks; where enabled, the AI path produces a draft only and never publishes a result itself.",
        capabilityKeys: ["report-card-ai", "result-publishing"],
      },
      {
        title: "Publish the school-approved artifact",
        description: "Generate the supported branded PDF and make the authorised published card available through the configured path.",
        capabilityKeys: ["result-publishing", "export-pdf"],
      },
    ],
    primaryCapabilityKeys: ["result-publishing", "report-card-ai", "student-performance", "digital-gradebook"],
    relatedCapabilityKeys: ["attendance-reports", "academic-reports", "export-pdf", "teacher-app", "parent-app", "student-app"],
    users: ["Class teachers", "Academic coordinators", "Exam team", "School leadership", "Families through published access"],
    whatIncluded: [
      { title: "Configurable school report cards", detail: "Supported academic, attendance and co-scholastic structures can be rendered through the reviewed report-card path." },
      { title: "Teacher remarks and reviewable AI draft", detail: "The human reviewer remains responsible for the narrative and final publication decision." },
      { title: "Branded PDF and authorised publication", detail: "The output follows the configured school template and supported publishing workflow." },
    ],
    conditions: [
      { title: "Not a universal PARAKH implementation", detail: "No complete stage-by-stage official template pack or compliance guarantee is asserted." },
      { title: "No peer/parent multi-rater engine", detail: "Peer assessment, parent feedback, automatic weighting and bias-reduction claims are outside the reviewed scope." },
      { title: "No automatic final assessment or publishing", detail: "Draft ratings or narrative remain review material; authorised school staff decide the final assessment, remarks and publication." },
    ],
    faqs: [
      { q: "Is this a complete PARAKH-compliant HPC system?", a: "No blanket compliance claim is made. The product supports configurable NEP/HPC-style school reporting; the exact stage, board and template requirements must be reviewed." },
      { q: "Does Aira make the final assessment?", a: "No. Aira can calculate draft domain ratings and narrative from supported evidence, but it does not save, approve or publish the final assessment." },
      { q: "Can the card include co-scholastic information?", a: "Supported configured co-scholastic domains and ratings can be included where the school has entered and frozen the applicable data." },
      { q: "Who publishes the card?", a: "An authorised school user publishes the configured result after review." },
    ],
    aliases: ["holistic progress card", "HPC", "NEP report card", "whole child report", "PARAKH progress card"],
    searchTerms: ["report card", "co-scholastic", "teacher remark", "progress", "NEP", "PDF", "result publish"],
    seoDescription: "Review Vidyapeeth360 configurable holistic progress cards, supported evidence, teacher remarks, PDF output and publication boundaries.",
    disclosure: "The guide covers configurable school-reviewed reporting, not a complete PARAKH template guarantee, multi-rater engine or autonomous assessment.",
  }),

  guide({
    key: "apaar-readiness",
    title: "APAAR Readiness, Consent & Import",
    shortTitle: "APAAR Readiness",
    eyebrow: "India data-readiness guide",
    type: "capability-guide",
    status: "Deterministic readiness and import scope",
    summary: "Review APAAR data-readiness findings, record purpose-specific consent evidence, export correction work and import an APAAR ID after it has been generated through the government process.",
    problem: "Schools need a defensible way to find record gaps and preserve consent evidence without confusing product readiness support with government ID generation or live registry verification.",
    outcome: "A deterministic preparation and post-generation import trail that keeps school correction work, consent evidence and government-system boundaries explicit.",
    workflow: [
      {
        title: "Run the readiness review",
        description: "Deterministic rules identify supported missing or malformed student fields and consent gaps for authorised staff review.",
        capabilityKeys: ["compliance-ai"],
      },
      {
        title: "Correct the school record",
        description: "Authorised teams review the deterministic finding and fix hint in the school's source record. A cited plain-language explanation requires enabled Aira scope and credits.",
        capabilityKeys: ["compliance-ai"],
      },
      {
        title: "Manage consent evidence and export",
        description: "Use the supported APAAR consent ledger and export path for the school's reviewed preparation work.",
        capabilityKeys: ["compliance-ai"],
      },
      {
        title: "Import the generated APAAR ID",
        description: "After the government process has generated the identifier, preview and import it into the matched, non-archived student record.",
        capabilityKeys: ["compliance-ai"],
      },
    ],
    primaryCapabilityKeys: ["compliance-ai"],
    relatedCapabilityKeys: ["student-admission", "document-management", "export-excel", "role-and-permission"],
    users: ["Authorised compliance team", "School administration", "Student-record team", "School leadership"],
    whatIncluded: [
      { title: "Deterministic readiness findings", detail: "Supported rules surface record and consent gaps with a fix hint; a cited explanation is available only when Aira scope and credits are enabled." },
      { title: "APAAR consent evidence", detail: "The purpose-specific ledger can record, preview, log and export the supported school workflow." },
      { title: "Post-generation import", detail: "Authorised staff can preview and import APAAR IDs after they have been generated through the government process." },
    ],
    conditions: [
      { title: "Vidyapeeth360 does not generate APAAR IDs", detail: "The product prepares records and imports the identifier after the government process." },
      { title: "No live UDISE+, Aadhaar or ABC synchronisation", detail: "Readiness and school-record correction are not official government verification or live account synchronisation." },
      { title: "No live DigiLocker claim", detail: "Existing adapter foundations are not marketed as an authorised live DigiLocker integration." },
    ],
    faqs: [
      { q: "Can Vidyapeeth360 generate an APAAR ID?", a: "No. The school completes the applicable government process; Vidyapeeth360 supports preparation, consent evidence and importing the generated ID." },
      { q: "Does the readiness percentage prove government acceptance?", a: "No. It is a deterministic product readiness view over supported school records, not a UDISE+ or APAAR authority decision." },
      { q: "Does the product verify Aadhaar details?", a: "No live Aadhaar verification or official name/DOB match is marketed in this scope." },
      { q: "Is consent permanent?", a: "No such claim is made. The school must operate the consent, withdrawal, retention and data-rights process that applies to its context." },
    ],
    aliases: ["APAAR ID software", "APAAR management", "APAAR readiness", "UDISE readiness", "APAAR consent"],
    searchTerms: ["APAAR", "PEN", "consent", "UDISE", "readiness", "government export", "ID import"],
    seoDescription: "Review Vidyapeeth360 APAAR readiness checks, consent evidence, correction export and post-government-generation ID import scope.",
    disclosure: "The guide covers deterministic preparation and post-generation import; it does not claim government ID generation or live registry verification.",
  }),
];

export const MODULE_BUYER_GUIDES = deepFreeze(GUIDES);

export const moduleBuyerGuideByKey = deepFreeze(Object.fromEntries(
  MODULE_BUYER_GUIDES.map((item) => [item.key, item]),
));

export function moduleBuyerGuideFor(key) {
  return moduleBuyerGuideByKey[String(key || "").trim().toLowerCase()] || null;
}

export function buyerGuidesForCapability(capabilityKey) {
  const key = String(capabilityKey || "").trim().toLowerCase();
  if (!key) return [];
  return MODULE_BUYER_GUIDES.filter((item) => (
    item.primaryCapabilityKeys.includes(key) || item.relatedCapabilityKeys.includes(key)
  ));
}

export function searchModuleBuyerGuides(query) {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return [];
  return MODULE_BUYER_GUIDES.filter((item) => [
    item.key,
    item.title,
    item.shortTitle,
    item.eyebrow,
    item.summary,
    item.problem,
    item.outcome,
    ...item.aliases,
    ...item.searchTerms,
    ...item.primaryCapabilityKeys,
    ...item.relatedCapabilityKeys,
  ].join(" ").toLowerCase().includes(needle));
}

export default MODULE_BUYER_GUIDES;
